import React from "react"
import { useSelector } from "react-redux"
import { selectMenuStates } from "../../store/reducers/menuSlice"
import { selectUserState } from "../../store/reducers/selectedUserSlice"
import { selectNavStates } from "../../store/reducers/navSlice"
import { dateFormatter } from "../../utils/dateFormatter"
import {User} from '../../models/user.model'
import UserContractConnectionManager from "./UserContractConnectionManager"
import { toast } from "react-toastify"
import { Spinner } from "../../utils/Spinner"
import { Company } from "../../models/company.model"
import { getCompanyDetails } from "../../api/getCompanyDetails"
import { Organization } from "../../models/organization.model"
import SelectedUser from "./SelectedUser"
import { selectOrgState } from "../../store/reducers/selectedOrgSlice"
import SelectOrg from "./SelectOrg"
import OrgContractConnectionManager from "./OrgContractConnectionManager"
import { makeContract } from "../../api/makeContract"
import { DaisyContractDetail } from "../../types/daisyContractDetail"
import { Envelope } from "../../models/envelope.model"
import { getUserEnvelopes } from "../../api/getUserEnvelopes"
import { currencyFormatter } from "../../utils/currencyFormatter"
import { BankAccount } from "../../models/bank.account.model"
import { CryptoAccount } from "../../models/crypto.account.model"

export default function ShowContractDetails () {

    const menu = useSelector(selectMenuStates)
    const nav = useSelector(selectNavStates)
    const select = useSelector(selectUserState)
    const org = useSelector(selectOrgState)

    const [spinner, setSpinner] = React.useState<boolean>(false)

    const [selectedUserDetails, setSelectedUserDetails] = React.useState<User>()
    const [selectedOrgDetails, setSelectedOrgDetails] = React.useState<Organization>()

    const [companyDetails, setCompanyDetails] = React.useState<Company>()
    const [currencyOption, setCurrencyOption] = React.useState<string>("EUR");

    const [amount, setAmount] = React.useState<number>(0);
    const [lendAmount, setLendAmount] = React.useState<number>(0);

    const [envelopeRefId, setEnvelopeRefId] = React.useState<string | undefined>("");
    const [docuUri, setDocuUri] = React.useState<string>("");

    const [connectedEnvelopes, setConnectedEnvelopes] = React.useState<Envelope[]>([]); // store envelopes

    const currencies = ["EUR", "USD", "USDT", "USDC"];
    const [selectedCurrency, setSelectedCurrency] = React.useState<string>("EUR");

    const [selectedAccount, setSelectedAccount] = React.useState<BankAccount | CryptoAccount | null>();
    const [accType, setAccType] = React.useState<boolean>(true);

    const handleCurrencyOption = (currency: string) => {
        setSelectedCurrency(currency);
    };

    const handleAmountChanges = (amount: string) => {
        const validAmount = amount.replace(/[,.]/g, "");
        setAmount(parseInt(validAmount, 10))
    }

    const handleLendAmountChanges = (amount: string) => {
        const validAmount = amount.replace(/[,.]/g, "");
        setLendAmount(parseInt(validAmount, 10))
    }

    const copyToClipBoard = () => {
        const dummyTextArea = document.createElement('textarea');
        dummyTextArea.value = docuUri;
        document.body.appendChild(dummyTextArea);
        dummyTextArea.select();
        // @ts-ignore
        document.execCommand('copy');
        document.body.removeChild(dummyTextArea);
    }

    /* Effects */
    React.useEffect(() => {
        setSelectedUserDetails(select)
        setSelectedOrgDetails(org)
    }, [select, org])

    React.useEffect(() => {
        getCompanyDetails()
        .then((response) => {
            setCompanyDetails(response)
        })
        .catch((error) => {
            console.error(error);
        });
    }, []);

    React.useEffect(() => {
        if(selectedUserDetails && selectedOrgDetails) {
            return;
        }


        const fetchEnvelopes = async () => {
            try {
                if(selectedUserDetails) {
                    const result = await getUserEnvelopes(selectedUserDetails?.user_id);
                    setConnectedEnvelopes(result)
                } 
    
                if(selectedOrgDetails) {
                    const result = await getUserEnvelopes(selectedOrgDetails?.leader?.user_id || -1);
                    setConnectedEnvelopes(result)
                }

            } catch (err) {
                console.error(err);
            } finally {
                console.log("Everythings done.");
            }
        }

        fetchEnvelopes()
    }, [selectedUserDetails, selectedOrgDetails])

    const processContract = async (user: User | undefined, org: Organization | undefined, company: Company | undefined, currency: string, amount: number, lendAmount: number, template_ref_id: string | undefined, daisyContractDetail?: DaisyContractDetail) => {
        try {
            toast.info("A szerződés előállítása megkezdődött, kérlek várj...")
            console.log("a processbe: " + template_ref_id)
            const res = await makeContract(
                user, 
                org, 
                company, 
                currency, 
                amount,
                lendAmount,
                template_ref_id,
                selectedAccount,
                envelopeRefId || undefined,
            );

            if(res) {
                setDocuUri(
                    res
                );
            }
        } catch (err) {
            console.error(err)
        }
    }

    if(!menu.templateMenu) return <p className="text-center text-dark-blue text-lg">Válassz egy szerződést, vagy hozz létre egy újat!</p>;

    return (
        <>
            <div className="rounded-lg shadow-lg bg-dark-blue text-white py-2 px-2 flex">
                
                <div className="flex items-center">
                    <img src="/svg/contract.svg" width={85} height={85} className="mr-2 p-2" />
                    <div>
                        <h1 className="text-lg mb-2">Sablon neve: <span className="text-cream">{menu?.templateMenu?.name}</span></h1>
                        <p className="text-md">Sablon készült: <span className="text-cream">{dateFormatter(menu?.templateMenu?.created.toString() || '')}</span></p>
                        <p className="text-md">Sablon tárgya: <span className="text-cream">{menu?.templateMenu?.emailSubject}</span></p>
                        <p className="text-md">Sablon szövege: <span className="text-cream">{menu?.templateMenu?.emailBlurb}</span></p>
                        <p className="text-md">Sablon oldalainak száma: <span className="text-cream">{menu?.templateMenu?.pageCount}</span></p>
                        <p className="text-md">Sablon azonosítója: <span className="text-cream">{menu?.templateMenu?.templateId}</span></p>

                        <a href="https://www.docusign.com/" target="_blank">
                            <button
                                className="hover:bg-dark-brown text-white bg-brown font-bold uppercase text-sm px-3 py-1 mt-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1"
                                type="button"
                            >
                                Sablon módosítása
                            </button>
                        </a>

                    </div>
                </div>
            </div>
            <div className="flex flex-row mt-10">
                <div className="rounded-lg shadow-lg bg-dark-blue text-white py-2 px-2 w-2/4 mr-3">
                    <div className="flex items-center">
                        <img src="/svg/user.svg" width={85} height={85} className="mr-2 p-2" />
                        <div className="mt-2">
                            {menu?.templateMenu?.template_applicant === 0 ? (
                                <>
                                    <SelectedUser select={select}/>
                                    <UserContractConnectionManager user={selectedUserDetails} template={menu.templateMenu} title={select ? 'Felhasználó cseréje' : 'Felhasználó hozzárendelése'} color={select ? 'brown' : 'success'}/>
                                </>
                            ) : (
                                <>
                                    <SelectOrg select={org}/>
                                    <OrgContractConnectionManager template={menu.templateMenu} color={org ? 'brown' : 'success'} title={org ? "Cég cseréje" : 'Cég hozzárendelése'} org={selectedOrgDetails}/>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <div className="rounded-lg shadow-lg bg-dark-blue text-white py-2 px-2 w-2/4 ml-3">
                    <div className="flex items-center">
                        <img src="/svg/download.svg" width={85} height={85} className="mr-2 p-2" />
                        <div className="mt-2">
                            <p className="text-cream">Személyre szabott szerződés elkészítése</p>
                            <div className="">

                                {menu?.templateMenu?.templateId === process.env.REACT_APP_TEMPLATE_HDC_BAZ && (
                                    <>
                                    <label className="block text-white text-sm font-bold mt-5" htmlFor="amount">
                                    <span className="font-semibold">Kölcsönszerződésen</span> szereplő összeg:
                                    </label>
                                    <input
                                        onChange={(e) => handleLendAmountChanges(e.target.value)}
                                        className="shadow appearance-none border rounded w-full mt-2 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" id="amount" type="number" placeholder="Add meg az összeget."/>   
                                    
                                    </>
                                )}

                                <label className="block text-white text-sm font-bold mt-5" htmlFor="amount">
                                <span className="font-semibold">Szerződésen</span> szereplő összeg:
                                </label>
                                <input
                                    onChange={(e) => handleAmountChanges(e.target.value)}
                                    className="shadow appearance-none border rounded w-full mt-2 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" id="amount" type="number" placeholder="Add meg az összeget."/>

                                <ul className="flex items-center mt-5">
                                    {currencies.map((currency, index) => (
                                        <li key={index}>
                                        <input
                                            id={currency}
                                            value={currency}
                                            checked={currency === selectedCurrency}
                                            onChange={(e) => handleCurrencyOption(e.target.value)}
                                            type="radio"
                                            className="focus:outline-none w-4 h-4"
                                        />
                                        <label
                                            htmlFor={currency}
                                            className="ml-2 mr-2 text-lg font-medium text-white "
                                        >
                                            {currency}
                                        </label>
                                        </li>
                                    ))}
                                     <li>
                                        <>
                                        <Spinner show={spinner} width={4} height={4}/>

                                        {!spinner && (
                                            <button 
                                            onClick={() => {
                                                if(select && !org) {
                                                    processContract(select, org, companyDetails, selectedCurrency, amount, lendAmount, menu?.templateMenu?.templateId || "not_found");
                                                }

                                                if(org && !select) {
                                                    processContract(select, org, companyDetails, selectedCurrency, amount, lendAmount, menu?.templateMenu?.templateId || "not_found");
                                                }
                                            }}
                                            className={`enabled:hover:text-cream text-white bg-error font-bold uppercase text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none disabled:bg-error disabled:opacity-70 enabled:bg-success`} disabled={(amount > 0 && (select || org)) ? false : true}>Szerződés küldése</button>
                                        )}
                                        </>
                                        
                                    </li>
                                </ul>
                                
                            </div>
                            <div className="mb-3 items-center text-lg font-medium rounded-lg sm:flex h-8 mt-2">
                                <p className="text-md">DocuSign URL: <a className="text-cream w-fit" href={docuUri} target="_blank">{docuUri.length === 0 ? 'Még nincs link előállítva!' : "Tovább a DocuSign-ra!"}</a></p>
                                {docuUri.length > 0 && (
                                    <button
                                        className="ml-2 hover:bg-dark-brown text-white bg-brown font-bold uppercase text-sm px-3 py-1 mt-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1"
                                        type="button"
                                        onClick={copyToClipBoard}
                                    >
                                        Link másolása
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        
        
            {(select || org) && (
                <div className="flex bg-dark-blue flex-col mt-10 gap-6 p-6 rounded-xl shadow-lg">

                    {/* Section Title (Optional, but improves structure) */}
                    <h3 className="text-white font-semibold text-lg">
                    Számla vagy tárca kiválasztása
                    </h3>

                    {/* Toggle between Bank / Crypto */}
                    <label className="flex items-center gap-3 text-white cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={accType}
                        onChange={(e) => {
                        setAccType(e.target.checked);
                        setSelectedAccount(null);
                        }}
                        className="w-4 h-4 accent-blue-500 cursor-pointer"
                    />
                    <span>
                        Bankszámlaszámok megjelenítése (ha nincs bepipálva: Kriptotárcák)
                    </span>
                    </label>

                    {/* Dropdown */}
                    <div className="flex flex-col">
                    <label className="mb-2 text-sm text-gray-300">Válassz egyet:</label>
                    <select
                        className="rounded-lg bg-[#1b2b4b] border border-gray-600 text-white py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        value={selectedAccount?.id?.toString() || ""}
                        onChange={(e) => {
                        const list = accType
                            ? (select?.BankAccount ?? org?.BankAccount)
                            : (select?.CryptoAccount ?? org?.CryptoAccount);

                        const chosen = (list as (BankAccount | CryptoAccount)[] | undefined)
                            ?.find((acc) => acc.id.toString() === e.target.value) || null;

                        setSelectedAccount(chosen);
                        }}
                    >
                        <option value="">Válassz egy számlát/tárcát...</option>
                        {(accType
                        ? (select?.BankAccount?.length
                            ? select.BankAccount
                            : org?.BankAccount)
                        : (select?.CryptoAccount?.length
                            ? select.CryptoAccount
                            : org?.CryptoAccount)
                        )?.map((acc) => (
                        <option key={acc.id} value={acc.id.toString()}>
                            {"iban" in acc
                            ? `${acc.iban} – ${acc.name}`
                            : `${acc.address} – ${acc.network}`}
                        </option>
                        ))}
                    </select>
                    </div>

                </div>
            )}


        <div className="flex flex-row mt-10">
            <div className="rounded-xl shadow-lg bg-dark-blue text-white py-6 px-6 w-full">
                <div className="flex items-start gap-5">

                {/* User icon */}
                <img
                    src="/svg/user.svg"
                    width={85}
                    height={85}
                    className="p-2 rounded-lg bg-[#1b2b4b]"
                />

                {/* Content */}
                <div className="flex flex-col w-full">
                    {/* Title */}
                    <p className="text-gray-200 text-md mb-2 font-medium">
                    Kérlek válassz kapcsolódó szerződést:
                    </p>

                    {/* Current ID information */}
                    <p className="text-sm text-gray-400 mb-4">
                    Jelenlegi ID:{" "}
                    <span className="text-gray-300 font-semibold">
                        {envelopeRefId ? envelopeRefId : "Nincs kiválasztva."}
                    </span>
                    </p>

                    {/* Dropdown */}
                    <label className="text-sm text-gray-300 mb-2">Válaszd ki a szerződést:</label>
                    <select
                    className="rounded-lg bg-[#1b2b4b] border border-gray-600 text-white py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    onChange={(e) => {
                        const selectedEnvelope = connectedEnvelopes.find(
                        env => env.envelope_ref_id === e.target.value
                        );
                        setEnvelopeRefId(selectedEnvelope?.envelope_ref_id);
                        console.log("Selected Envelope:", selectedEnvelope?.envelope_ref_id);
                    }}
                    >
                    <option value="">Nincs kiválasztva</option>

                    {connectedEnvelopes.map((envelope) => (
                        <option key={envelope.envelope_ref_id} value={envelope.envelope_ref_id}>
                        {envelope.depositRegister 
                            ? `[${envelope.depositRegister.date.toString().split('T')[0]}] - ${envelope.signer.name} - (${envelope.envelope_ref_id}) - (${currencyFormatter(envelope.depositRegister.invested_amount_eur, 'EUR')})`
                            : `${envelope.signer.name} - (${envelope.envelope_ref_id}) (Nincs társítva)`}
                        </option>
                    ))}
                    </select>
                </div>
            </div>
        </div>
        </div>


        </>
    )
}